import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Navbar from '../Page/Navbar';
import Footer from '../Page/Footer';
import staticHoroscopeService from '../../services/staticHoroscopeService';
import './HoroscopePage.css';

const HoroscopePage = () => {
    const [horoscopeData, setHoroscopeData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Liste des 12 signes astrologiques avec leurs icônes et périodes
    const signes = [
        { nom: 'Bélier', icon: 'aries.png', periode: '21 mars - 19 avril', element: 'Feu', planete: 'Mars' },
        { nom: 'Taureau', icon: 'taurus.png', periode: '20 avril - 20 mai', element: 'Terre', planete: 'Vénus' },
        { nom: 'Gémeaux', icon: 'gemini.png', periode: '21 mai - 20 juin', element: 'Air', planete: 'Mercure' },
        { nom: 'Cancer', icon: 'cancer.png', periode: '21 juin - 22 juillet', element: 'Eau', planete: 'Lune' },
        { nom: 'Lion', icon: 'leo.png', periode: '23 juillet - 22 août', element: 'Feu', planete: 'Soleil' },
        { nom: 'Vierge', icon: 'virgo.png', periode: '23 août - 22 septembre', element: 'Terre', planete: 'Mercure' },
        { nom: 'Balance', icon: 'libra.png', periode: '23 septembre - 22 octobre', element: 'Air', planete: 'Vénus' },
        { nom: 'Scorpion', icon: 'scorpio.png', periode: '23 octobre - 21 novembre', element: 'Eau', planete: 'Mars/Pluton' },
        { nom: 'Sagittaire', icon: 'sagittarius.png', periode: '22 novembre - 21 décembre', element: 'Feu', planete: 'Jupiter' },
        { nom: 'Capricorne', icon: 'capricorn.png', periode: '22 décembre - 19 janvier', element: 'Terre', planete: 'Saturne' },
        { nom: 'Verseau', icon: 'aquarius.png', periode: '20 janvier - 18 février', element: 'Air', planete: 'Uranus' },
        { nom: 'Poissons', icon: 'pisces.png', periode: '19 février - 20 mars', element: 'Eau', planete: 'Neptune' }
    ];

    // FAQ pour le SEO
    const faqData = [
        {
            question: "Comment lire son horoscope hebdomadaire ?",
            answer: "Pour lire votre horoscope hebdomadaire, trouvez votre signe astrologique selon votre date de naissance. Chaque prédiction est basée sur les positions planétaires actuelles et leurs influences sur votre signe. Lisez attentivement les conseils généraux et adaptez-les à votre situation personnelle."
        },
        {
            question: "À quelle fréquence l'horoscope hebdomadaire est-il mis à jour ?",
            answer: "Notre horoscope hebdomadaire est mis à jour chaque semaine avec de nouvelles prédictions basées sur les transits planétaires actuels. Nous publions généralement les nouvelles prédictions le dimanche pour la semaine à venir."
        },
        {
            question: "Sur quoi se basent les prédictions astrologiques ?",
            answer: "Nos prédictions se basent sur l'analyse des transits planétaires, des aspects astrologiques, et des influences des planètes sur chaque signe du zodiaque. Nous étudions les positions du Soleil, de la Lune, et des planètes pour vous offrir des prédictions précises."
        },
        {
            question: "Comment connaître son signe astrologique ?",
            answer: "Votre signe astrologique est déterminé par votre date de naissance. Le Soleil traverse chaque signe pendant environ un mois. Par exemple, si vous êtes né entre le 21 mars et le 19 avril, vous êtes Bélier. Consultez notre liste des signes avec leurs périodes pour le découvrir."
        },
        {
            question: "L'horoscope peut-il vraiment prédire l'avenir ?",
            answer: "L'horoscope offre des tendances et influences générales basées sur les mouvements planétaires. Il ne prédit pas l'avenir de manière absolue, mais propose des conseils et orientations pour vous aider à naviguer les énergies cosmiques de la semaine."
        }
    ];

    useEffect(() => {
        fetchLatestHoroscope();
        
        // Si il y a un hash dans l'URL, attendre que les données soient chargées avant de scroller
        const hash = window.location.hash;
        if (hash) {
            // Attendre un délai pour que les données soient rendues
            setTimeout(() => {
                const element = document.querySelector(hash);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 500);
        } else {
            window.scrollTo(0, 0);
        }
    }, []);

    const fetchLatestHoroscope = async () => {
        try {
            setLoading(true);
            const data = await staticHoroscopeService.getLatestHoroscope();

            if (data.success) {
                setHoroscopeData(data.data);
            } else {
                setError('Aucun horoscope disponible pour le moment');
            }
        } catch (err) {
            setError('Erreur lors du chargement de l\'horoscope');
            console.error('Erreur horoscope:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (timestamp) => {
        const date = new Date(timestamp * 1000);
        const options = { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        };
        return date.toLocaleDateString('fr-FR', options);
    };

    const getHoroscopeForSign = (signe) => {
        if (!horoscopeData || !horoscopeData.horoscopes_data || !horoscopeData.horoscopes_data.horoscopes) {
            return null;
        }
        return horoscopeData.horoscopes_data.horoscopes[signe];
    };

    const handleNavigationClick = (e, signeNom) => {
        e.preventDefault();
        const element = document.getElementById(signeNom.toLowerCase());
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            // Mettre à jour l'URL avec le hash
            window.history.pushState(null, null, `#${signeNom.toLowerCase()}`);
        }
    };

    // Génération des schémas JSON-LD
    const generateJSONLD = () => {
        const currentDate = horoscopeData ? new Date(horoscopeData.timestamp_creation * 1000).toISOString() : new Date().toISOString();
        
        const websiteSchema = {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "AstraLight",
            "url": "https://astralight.xyz",
            "potentialAction": {
                "@type": "SearchAction",
                "target": "https://astralight.xyz/search?q={search_term_string}",
                "query-input": "required name=search_term_string"
            }
        };

        const breadcrumbSchema = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
                {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Accueil",
                    "item": "https://astralight.xyz"
                },
                {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Horoscope Hebdomadaire",
                    "item": "https://astralight.xyz/horoscope-hebdomadaire"
                }
            ]
        };

        const articleSchema = {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Horoscope Hebdomadaire - Prédictions Astrologiques Détaillées",
            "description": "Découvrez votre horoscope hebdomadaire détaillé pour tous les signes du zodiaque. Analyses astrologiques approfondies basées sur les positions planétaires actuelles.",
            "image": "https://astralight.xyz/images/horoscope-hebdomadaire.jpg",
            "author": {
                "@type": "Organization",
                "name": "AstraLight"
            },
            "publisher": {
                "@type": "Organization",
                "name": "AstraLight",
                "logo": {
                    "@type": "ImageObject",
                    "url": "https://astralight.xyz/images/logo.png"
                }
            },
            "datePublished": currentDate,
            "dateModified": currentDate,
            "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": "https://astralight.xyz/horoscope-hebdomadaire"
            }
        };

        const faqSchema = {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqData.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.answer
                }
            }))
        };

        return [websiteSchema, breadcrumbSchema, articleSchema, faqSchema];
    };

    if (loading) {
        return (
            <>
                <Navbar />
                <main className="horoscope-page">
                    <div className="container">
                        <div className="horoscope-loading panel">
                            <p>Chargement des horoscopes...</p>
                        </div>
                    </div>
                </main>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Navbar />
                <main className="horoscope-page">
                    <div className="container">
                        <div className="horoscope-error panel">
                            <p>{error}</p>
                            <Link to="/" className="back-home-btn">Retour à l'accueil</Link>
                        </div>
                    </div>
                </main>
                <Footer />
            </>
        );
    }

    const jsonLDSchemas = generateJSONLD();

    return (
        <>
            <Helmet>
                <title>Horoscope Hebdomadaire | AstraLight - Prédictions Astrologiques Détaillées</title>
                <meta name="description" content="Découvrez votre horoscope hebdomadaire détaillé pour tous les signes du zodiaque. Analyses astrologiques approfondies basées sur les positions planétaires actuelles. Prédictions gratuites et précises." />
                <meta name="keywords" content="horoscope hebdomadaire, astrologie, prédictions, signes du zodiaque, planètes, influences astrales, bélier, taureau, gémeaux, cancer, lion, vierge, balance, scorpion, sagittaire, capricorne, verseau, poissons" />
                <link rel="canonical" href="https://astralight.xyz/horoscope-hebdomadaire" />
                
                {/* Open Graph / Facebook */}
                <meta property="og:type" content="article" />
                <meta property="og:title" content="Horoscope Hebdomadaire | AstraLight - Prédictions Astrologiques" />
                <meta property="og:description" content="Découvrez votre horoscope hebdomadaire détaillé pour tous les signes du zodiaque. Analyses astrologiques approfondies basées sur les positions planétaires actuelles." />
                <meta property="og:image" content="https://astralight.xyz/images/horoscope-hebdomadaire-og.jpg" />
                <meta property="og:url" content="https://astralight.xyz/horoscope-hebdomadaire" />
                <meta property="og:site_name" content="AstraLight" />
                <meta property="og:locale" content="fr_FR" />
                
                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Horoscope Hebdomadaire | AstraLight" />
                <meta name="twitter:description" content="Découvrez votre horoscope hebdomadaire détaillé pour tous les signes du zodiaque." />
                <meta name="twitter:image" content="https://astralight.xyz/images/horoscope-hebdomadaire-twitter.jpg" />
                
                {/* Additional meta tags */}
                <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
                <meta name="author" content="AstraLight" />
                <meta name="publisher" content="AstraLight" />
                <link rel="alternate" hreflang="fr" href="https://astralight.xyz/horoscope-hebdomadaire" />
                
                {/* JSON-LD Schema */}
                {jsonLDSchemas.map((schema, index) => (
                    <script key={index} type="application/ld+json">
                        {JSON.stringify(schema)}
                    </script>
                ))}
            </Helmet>

            <Navbar />

            <main className="horoscope-page">
                <div className="container">
                    {/* Breadcrumbs */}
                    <nav className="breadcrumbs" aria-label="Fil d'Ariane">
                        <Link to="/" className="breadcrumb-item">Accueil</Link>
                        <span className="breadcrumb-separator">›</span>
                        <span className="breadcrumb-current">Horoscope Hebdomadaire</span>
                    </nav>

                    <header className="horoscope-page-header">
                    <h1>Horoscope Hebdomadaire</h1>
                    <h2> Prédictions Astrologiques Détaillées</h2>
                    {horoscopeData && (
                            <p className="horoscope-page-date">
                                Semaine du {formatDate(horoscopeData.timestamp_creation)}
                            </p>
                        )}
                        <div className="horoscope-page-intro">
                            <p className="horoscope-page-subtitle">
                                Découvrez les influences astrologiques de la semaine pour chaque signe du zodiaque
                            </p>
                            <p className="horoscope-page-description">
                                Nos prédictions astrologiques hebdomadaires sont basées sur l'analyse des transits planétaires 
                                et des aspects astrologiques actuels. Chaque horoscope est rédigé avec le plus grand soin pour vous offrir des conseils personnalisés selon votre signe astrologique.
                            </p>
                        </div>
                    </header>

                    <nav className="horoscope-navigation panel">
                        <h2>Navigation rapide par signe astrologique</h2>
                        <div className="horoscope-nav-grid">
                            {signes.map((signe) => (
                                <a 
                                    key={signe.nom} 
                                    href={`#${signe.nom.toLowerCase()}`}
                                    className="horoscope-nav-item"
                                    onClick={(e) => handleNavigationClick(e, signe.nom)}
                                    title={`Horoscope ${signe.nom} - ${signe.periode}`}
                                >
                                    <span className="nav-symbole">
                                        <img src={`/images/signes_icons/${signe.icon}`} alt={`Symbole ${signe.nom}`} />
                                    </span>
                                    <span className="nav-nom">{signe.nom}</span>
                                    <span className="nav-periode">{signe.periode}</span>
                                </a>
                            ))}
                        </div>
                    </nav>

                    <div className="horoscope-detailed-list">
                        <h2 className="sr-only">Horoscopes détaillés par signe</h2>
                        {signes.map((signe, index) => {
                            const horoscope = getHoroscopeForSign(signe.nom);
                            
                            return (
                                <article key={signe.nom} id={signe.nom.toLowerCase()} className="horoscope-detailed-item panel">
                                    <header className="horoscope-detailed-header">
                                        <div className="signe-symbole-large">
                                            <img src={`/images/signes_icons/${signe.icon}`} alt={`Symbole astrologique ${signe.nom}`} />
                                        </div>
                                        <div className="signe-info">
                                            <h3>Horoscope {signe.nom}</h3>
                                            <div className="signe-details">
                                                <span className="signe-periode">{signe.periode}</span>
                                                <span className="signe-element">Élément : {signe.element}</span>
                                                <span className="signe-planete">Planète : {signe.planete}</span>
                                            </div>
                                        </div>
                                    </header>

                                    {horoscope ? (
                                        <div className="horoscope-detailed-content">
                                            <div className="horoscope-detailed-text">
                                                {horoscope.horoscope_detaille?.split('\n').map((paragraph, paragraphIndex) => (
                                                    paragraph.trim() && (
                                                        <p key={paragraphIndex}>{paragraph.trim()}</p>
                                                    )
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="horoscope-unavailable">
                                            <p>Horoscope temporairement indisponible pour le signe {signe.nom}</p>
                                            <p>Revenez bientôt pour découvrir vos prédictions astrologiques.</p>
                                        </div>
                                    )}
                                </article>
                            );
                        })}
                    </div>

                    {/* Section FAQ */}
                    <section className="horoscope-faq panel">
                        <h2>Questions Fréquentes sur l'Horoscope Hebdomadaire</h2>
                        <div className="faq-list">
                            {faqData.map((faq, index) => (
                                <div key={index} className="faq-item">
                                    <h3 className="faq-question">{faq.question}</h3>
                                    <p className="faq-answer">{faq.answer}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Section informative supplémentaire */}
                    <section className="horoscope-info panel">
                        <h2>À propos de nos prédictions astrologiques</h2>
                        <div className="info-content">
                            <p>
                                Nos horoscopes hebdomadaires sont élaborés par une équipe d'astrologues expérimentés qui analysent 
                                minutieusement les transits planétaires, les aspects astrologiques et les influences cosmiques 
                                de chaque semaine. Nous prenons en compte les positions du Soleil, de la Lune, de Mercure, Vénus, 
                                Mars, Jupiter, Saturne, Uranus, Neptune et Pluton pour vous offrir des prédictions précises.
                            </p>
                            <p>
                                Chaque signe astrologique est influencé différemment par ces énergies cosmiques. Nos prédictions 
                                vous aident à comprendre ces influences et à naviguer au mieux dans votre semaine, que ce soit 
                                en amour, travail, santé ou finances.
                            </p>
                            <div className="info-disclaimer">
                                <p>
                                    <strong>Avertissement :</strong> L'astrologie est un art divinatoire qui propose des tendances 
                                    et des conseils. Nos horoscopes sont donnés à titre indicatif et ne remplacent pas votre 
                                    libre arbitre ni les conseils de professionnels qualifiés.
                                </p>
                            </div>
                        </div>
                    </section>

                    <div className="horoscope-page-navigation">
                        <Link to="/" className="back-home-btn">
                            Retour à l'accueil
                        </Link>
                        <Link to="/blog" className="blog-link-btn">
                            Découvrir notre blog astrologique
                        </Link>
                    </div>
                </div>
            </main>

            <Footer />
        </>
    );
};

export default HoroscopePage; 